import React from "react";
import { Helmet } from "react-helmet-async";

import {
  DEFAULT_META,
  OG_IMAGE,
  PRODUCT,
  ROUTE_META,
  absoluteUrl,
} from "../seo/siteMeta";

/**
 * Per-route <head> content. Without this every route shipped the same title and
 * description, so search engines and answer engines saw five pages that all claimed
 * to be the same one.
 *
 * `jsonLd` accepts a schema.org object (or array of them) rendered as application/ld+json.
 */
const Seo = ({ route, title, description, noindex = false, jsonLd = null }) => {
  const meta = ROUTE_META[route] || {};
  const resolvedTitle = title || meta.title || DEFAULT_META.title;
  const resolvedDescription =
    description || meta.description || DEFAULT_META.description;
  const canonical = absoluteUrl(meta.path || route || "/");
  const graph = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  // The 404 page is served for every unknown path, so it has no single URL of its own to
  // be canonical to. Emitting one would point at a path that does not exist, and pairing a
  // canonical with noindex sends two contradictory signals. Omit it instead.
  const showCanonical = !noindex;

  return (
    <Helmet prioritizeSeoTags>
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      {showCanonical && <link rel="canonical" href={canonical} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={PRODUCT.name} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta
        property="og:image:alt"
        content="BadgeReg: merit badge college registration for Scout troops"
      />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={OG_IMAGE} />

      {graph.map((node, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(node)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
